import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useAlbumImageLoading } from './useAlbumImageLoading'

// useAlbumImageBatches 按布局分批创建图片，保留已加载批次
export const useAlbumImageBatches = <T extends object>(allItems: ComputedRef<T[]>, batchSize: ComputedRef<number>, enabled: Ref<boolean>, requiresImage: (item: T) => boolean) => {
  const sourceBatch = ref(0)
  const batchStart = ref(0)
  const requestedCount = ref(0)
  const items = computed(() => allItems.value.slice(0, requestedCount.value))
  const batchItems = computed(() => allItems.value.slice(batchStart.value, requestedCount.value))
  const { imagesSettled, markImageLoaded, markImageFailed, isImageFailed } = useAlbumImageLoading<T>(batchItems, requiresImage)
  const batchSet = computed(() => new Set(batchItems.value))
  const hasMore = computed(() => requestedCount.value < allItems.value.length)

  // 页面挂载并恢复布局后才创建首批图片
  watch([allItems, enabled], ([list, ready]) => {
    batchStart.value = 0
    requestedCount.value = ready ? Math.min(list.length, batchSize.value) : 0
    sourceBatch.value += 1
  }, { immediate: true, flush: 'sync' })

  // loadMore 当前批次完成后请求下一批图片
  const loadMore = () => {
    if (!enabled.value || !imagesSettled.value || !hasMore.value) return
    batchStart.value = requestedCount.value
    requestedCount.value = Math.min(allItems.value.length, requestedCount.value + batchSize.value)
  }

  // isBatchLoading 仅为尚未完成的新批次显示骨架
  const isBatchLoading = (item: T) => !imagesSettled.value && batchSet.value.has(item)

  return { items, sourceBatch, batchStart, imagesSettled, hasMore, loadMore, markImageLoaded, markImageFailed, isImageFailed, isBatchLoading }
}
